import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { chainLabel } from "../lib/chain";
import { timecode } from "../lib/scrub";
import { searchable } from "../lib/search";

/**
 * Command-K: go anywhere, or find anything, without navigating to it first.
 *
 * Searches the places you can go and the content of whichever space you are
 * in. Scoped to that space rather than everything, because the corpus is
 * per-space and a result you cannot open is noise.
 */
interface Item {
  id: string;
  label: string;
  hint?: string;
  group: string;
  /** Where choosing it goes. Absent for an item that does something instead. */
  to?: string;
  /**
   * What choosing it does, for the items that act rather than navigate. The
   * point of searching from inside a project is to grab material without
   * leaving the page, which a link cannot do (ADR 000051).
   */
  run?: () => void;
}

/** The space in the address, if the current page is inside one. */
export function spaceFromPath(pathname: string): string | null {
  return /^\/spaces\/(spc_[0-9A-Za-z]{11})/.exec(pathname)?.[1] ?? null;
}

/**
 * The project in the address, if the current page is inside one.
 *
 * What makes the shortlist reachable from the palette: the entries belong to a
 * project, so there has to be one to gather for before they mean anything.
 */
export function projectFromPath(pathname: string): string | null {
  return /\/projects\/(prj_[0-9A-Za-z]{11})/.exec(pathname)?.[1] ?? null;
}

// contentSearchDebounceMs is how long typing pauses before a manifest-content
// search fires, so scrolling through a query in progress does not cost a
// request per keystroke.
const contentSearchDebounceMs = 200;

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [contentQuery, setContentQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const spaceId = spaceFromPath(location.pathname);
  const projectId = projectFromPath(location.pathname);
  const queryClient = useQueryClient();
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Cmd on a Mac, Ctrl elsewhere, which is what everything else this sits
      // beside already uses.
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((was) => !was);
        setQuery("");
        setCursor(0);
        setDone(null);
      }
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (open) input.current?.focus();
  }, [open]);

  // Debounced separately from `query` itself, so typing does not fire a
  // manifest-content search per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setContentQuery(query.trim()), contentSearchDebounceMs);
    return () => clearTimeout(timer);
  }, [query]);

  // Only fetched while the palette is open, so having it mounted costs nothing.
  const spaces = useQuery({
    queryKey: ["spaces"],
    queryFn: () => api.listSpaces(),
    enabled: open,
  });
  const assets = useQuery({
    queryKey: ["assets", spaceId, ""],
    queryFn: () => api.listAssets(spaceId as never),
    enabled: open && spaceId !== null,
  });
  const projects = useQuery({
    queryKey: ["projects", spaceId],
    queryFn: () => api.listProjects(spaceId as never),
    enabled: open && spaceId !== null,
  });
  // The retired ones too, ranked below. This is where somebody who remembers a
  // project by name goes, and a name they have to search for is more often an
  // old one than a current one (ADR 000079).
  const archivedProjects = useQuery({
    queryKey: ["projects", spaceId, "archived"],
    queryFn: () => api.listProjects(spaceId as never, { archived: true }),
    enabled: open && spaceId !== null,
  });
  // A word only in an asset's transcript, summary or topics still has to be
  // findable (ADR 000045), and so does its name, which the index carries since
  // ADR 000094. Title matching stays instant and client-side below, over the
  // assets already loaded; this is the round trip that reaches the rest.
  const contentMatches = useQuery({
    queryKey: ["assets-search", spaceId, contentQuery],
    queryFn: () => api.listAssets(spaceId as never, { q: contentQuery }),
    // Not asked when the index could not answer it; the client-side name
    // match below still finds "AI ads" for "AI" (issue #178).
    enabled: open && spaceId !== null && searchable(contentQuery),
  });

  // Only while the palette is open, and only inside a project: a shortlist is
  // meaningless anywhere else.
  const shortlist = useQuery({
    queryKey: ["shortlist", spaceId, projectId],
    queryFn: () => api.listShortlist(spaceId as never, projectId as never),
    enabled: open && spaceId !== null && projectId !== null,
  });

  const promote = useMutation({
    mutationFn: (vars: { assetId: string; momentId: string }) =>
      api.promoteShortlistEntry(
        spaceId as never,
        projectId as never,
        vars.assetId as never,
        vars.momentId as never,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["timeline", spaceId, projectId] }),
  });

  const items = useMemo<Item[]>(() => {
    const out: Item[] = [
      { id: "go-spaces", label: "Spaces", group: "Go to", to: "/spaces" },
      { id: "go-settings", label: "Settings", group: "Go to", to: "/settings" },
      { id: "go-keys", label: "Agent keys", group: "Go to", to: "/settings/keys" },
    ];
    if (spaceId) {
      out.push({
        id: "go-content",
        label: "Content",
        group: "Go to",
        to: `/spaces/${spaceId}/content`,
      });
    }

    // First, so that material already gathered for this project outranks the
    // whole corpus: it is the half somebody already decided was worth using.
    for (const entry of shortlist.data ?? []) {
      const moment = entry.moment;
      if (!moment) continue;
      out.push({
        id: `shortlist:${entry.assetId}:${entry.momentId}`,
        label: moment.title,
        hint: `${timecode(moment.range.startSec)} · add to timeline`,
        group: "Shortlist",
        run: () => {
          promote.mutate(
            { assetId: entry.assetId, momentId: entry.momentId },
            { onSuccess: () => setDone(`Added "${moment.title}" to the timeline.`) },
          );
        },
      });
    }

    for (const space of spaces.data ?? []) {
      out.push({
        id: space.id,
        label: space.name,
        hint: space.role,
        group: "Spaces",
        to: `/spaces/${space.id}`,
      });
    }
    for (const project of projects.data ?? []) {
      out.push({
        id: project.id,
        label: project.name,
        hint: chainLabel(project.chain),
        group: "Projects",
        to: `/spaces/${spaceId}/projects/${project.id}`,
      });
    }
    for (const project of archivedProjects.data ?? []) {
      out.push({
        id: project.id,
        label: project.name,
        // Said in the hint rather than left to be discovered on the page, so
        // that the reason this one is not in the list above is on the row.
        hint: `archived · ${chainLabel(project.chain)}`,
        group: "Projects",
        to: `/spaces/${spaceId}/projects/${project.id}`,
      });
    }
    for (const asset of assets.data ?? []) {
      out.push({
        id: asset.id,
        label: asset.name,
        hint: asset.kind,
        group: "Content",
        to: `/spaces/${spaceId}/content/${asset.id}`,
      });
    }
    return out;
  }, [
    spaces.data,
    projects.data,
    archivedProjects.data,
    assets.data,
    shortlist.data,
    spaceId,
    promote,
  ]);

  const needle = query.trim().toLowerCase();

  // Content whose transcript, summary or topics matched, but whose name and
  // kind did not (ADR 000045) — everything else is the instant, client-side
  // filter this always was.
  const matchedByContent = useMemo(
    () => new Set((contentMatches.data ?? []).map((asset) => asset.id)),
    [contentMatches.data],
  );

  const shown = useMemo(() => {
    const matchesLocally = (item: Item) =>
      `${item.label} ${item.hint ?? ""}`.toLowerCase().includes(needle);
    return (
      needle
        ? items.filter(
            (item) =>
              matchesLocally(item) || (item.group === "Content" && matchedByContent.has(item.id)),
          )
        : items
    )
      .map((item) => {
        if (item.group !== "Content" || matchesLocally(item) || !matchedByContent.has(item.id))
          return item;
        return {
          ...item,
          hint: [item.hint, "mentioned in the transcript"].filter(Boolean).join(" · "),
        };
      })
      .slice(0, 40);
  }, [items, needle, matchedByContent]);

  if (!open) return null;

  const go = (item: Item) => {
    if (item.run) {
      item.run();
      // Left open on purpose: grabbing one moment is usually grabbing three,
      // and closing after each would mean reopening and retyping every time.
      return;
    }
    if (item.to) navigate(item.to);
    setOpen(false);
  };

  // Grouped for the eye, but the cursor walks the flat list, so arrow keys do
  // not have to know about headings.
  const groups: { name: string; items: Item[] }[] = [];
  for (const item of shown) {
    const last = groups.at(-1);
    if (last?.name === item.group) last.items.push(item);
    else groups.push({ name: item.group, items: [item] });
  }

  return (
    <div className="palette-backdrop" onPointerDown={() => setOpen(false)}>
      <div
        className="palette"
        role="dialog"
        aria-modal="true"
        aria-label="Search and go to"
        onPointerDown={(event) => event.stopPropagation()}
      >
        <input
          ref={input}
          value={query}
          placeholder={
            projectId
              ? "Search the shortlist, content, projects and spaces"
              : "Search content, projects and spaces"
          }
          aria-label="Search"
          onChange={(event) => {
            setQuery(event.target.value);
            setCursor(0);
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setCursor((c) => Math.min(c + 1, shown.length - 1));
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              setCursor((c) => Math.max(c - 1, 0));
            } else if (event.key === "Enter") {
              event.preventDefault();
              const chosen = shown[cursor];
              if (chosen) go(chosen);
            }
          }}
        />

        {done && <p className="palette-done">{done}</p>}

        {shown.length === 0 ? (
          <p className="palette-empty">Nothing matches {query}.</p>
        ) : (
          <div className="palette-results">
            {groups.map((group) => (
              <div key={group.name}>
                <p className="palette-group">{group.name}</p>
                {group.items.map((item) => {
                  const index = shown.indexOf(item);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={[index === cursor ? "is-cursor" : "", item.run ? "is-action" : ""]
                        .filter(Boolean)
                        .join(" ")}
                      onPointerEnter={() => setCursor(index)}
                      onClick={() => go(item)}
                    >
                      <span>{item.label}</span>
                      {item.hint && <span className="palette-hint">{item.hint}</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        <p className="palette-foot">
          <kbd>↑</kbd> <kbd>↓</kbd> to move · <kbd>enter</kbd> to open · <kbd>esc</kbd> to close
        </p>
      </div>
    </div>
  );
}
